import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { RootState } from "../../../../redux/store";
import {
  setRoomRentalLocation,
  checkInDateSelected,
  checkInDateSelectedClear,
  checkOutDateSelected,
  checkOutDateSelectedClear,
  startTimeSelected,
  endTimeSelected,
  eventTypeSelected,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import {
  setNumberOfGuest,
  setSelectedApartment,
  clearSelectedApartment,
  apartmentStepSet,
} from "../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import {
  parseBookingQuery,
  stringifyBookingQuery,
  withForeignParams,
} from "../url/bookingQuery";
import { BookingQuery, clampStep } from "../url/bookingQuerySchema";

/**
 * Bidirectional URL ↔ Redux sync for every room-shaped booking flow.
 *
 * Merged from the two copies under `roomRental/common/hooks/` and
 * `venueReservation/common/hooks/` (ADR-0026, phase 1 slice B), which were the
 * same hook with different key names and two extra fields on the venue side.
 *
 * ── Behaviour, unchanged from both copies ─────────────────────────────────
 *
 * - **URL → Redux** on every search change: initial mount, browser back and
 *   forward, a manually edited URL. Only fields that actually differ are
 *   dispatched.
 * - **Redux → URL** on every state change. A step change uses `push`, so the
 *   back button walks the step machine; a field change uses `replace`, so
 *   typing does not flood history.
 * - A `reconcilingRef` guard prevents oscillation: when URL → Redux has just
 *   dispatched, the Redux → URL effect skips one cycle rather than re-pushing
 *   the value the customer just left.
 * - `objectId` hydrates only once the object list has loaded — it is resolved
 *   against the fetched list, not stored in Redux as a bare string.
 *
 * ── What the options are, and what they are not ───────────────────────────
 *
 * `timeOfDay` and `eventType` are the first two of the curated module toggles
 * from the plan's §3.2 — not free-form flags. With a toggle off the hook does
 * not merely skip writing the field: it dispatches nothing for it at all, which
 * is what keeps a room-rental flow from clearing state it never owned.
 *
 * The date-picker's selection behaviour is NOT touched here and must not be.
 * Both flows guarantee `checkOutDate >= checkInDate` before it ever reaches
 * Redux (room clamps to `checkInDate + minNights`, venue takes react-day-picker's
 * already-normalised range and defaults a single click to the same day), so the
 * parser's "drop a check-out before the check-in" rule can only ever fire on a
 * hand-edited or stale URL — never on something the picker produced.
 */
export interface BookingUrlStateOptions {
  /** Persist start and end time of day. */
  timeOfDay?: boolean;
  /** Persist the event-type selection. */
  eventType?: boolean;
  /**
   * Object-first entry (#3035): the URL's `objectId` is the room the whole
   * flow is FOR, not a selection that comes and goes — keep it whatever the
   * selection does. See `QuerySource.pinnedObjectId`.
   */
  pinObjectId?: boolean;
}

/**
 * Apply a value the URL carries when Redux disagrees with it, reporting whether
 * anything was dispatched.
 *
 * Extracted because the reconciliation ran the identical compare-then-dispatch
 * shape eight times inline, which is both the complexity budget and the place a
 * typo would hide: eight chances to compare the wrong pair.
 */
function reconcile<T>(
  target: T,
  // `unknown`, not `T`: Redux holds some of these as optional while the URL
  // side always resolves to a concrete value, and the original code compared
  // the two RAW — so an unset `startTime` against the "0" sentinel counted as a
  // change and dispatched. Widening the parameter keeps that exact comparison
  // instead of quietly making the two equal.
  current: unknown,
  apply: (value: T) => void
): boolean {
  if (target === current) return false;
  apply(target);
  return true;
}

/**
 * The date variant: a date is either set from its `YYYY-MM-DD` string or
 * cleared, and Redux holds a moment while the URL holds a string, so the
 * comparison has to happen in the string domain.
 */
function reconcileDate(
  target: string | null,
  current: string | null,
  onSet: (value: moment.Moment) => void,
  onClear: () => void
): boolean {
  if (target === current) return false;
  if (target) onSet(moment(target, "YYYY-MM-DD"));
  else onClear();
  return true;
}

/** What the Redux → URL direction reads. Grouped into one argument so the
 *  effect stays a effect and the "which fields belong in the URL" decision
 *  lives in one testable place. */
export interface QuerySource {
  /** The entry category, read back out of the URL — see `buildQuery`. */
  category?: string;
  city: string | null;
  district: string | null;
  checkInDate: moment.Moment | null;
  checkOutDate: moment.Moment | null;
  startTime?: string;
  endTime?: string;
  eventType?: string | null;
  numberOfGuests: number;
  selectedObjectId: string | null;
  /**
   * An `objectId` that arrived in the URL before the object list loaded —
   * see `buildQuery`. Only set until the deferred hydration has run.
   */
  pendingObjectId?: string;
  /**
   * The room an object-first flow is scoped to (#3035), read back out of the
   * URL. Without it the id lived only as long as a selection: the selection
   * step clears the selection when it mounts, so coming back from the
   * overview — or a search that did not return the room — dropped it at the
   * next sync, and the flow fell back to the full list without a word. Leaving
   * the room is an explicit act (`backToRooms` takes it out of the URL).
   */
  pinnedObjectId?: string;
  step: number;
  timeOfDay: boolean;
  withEventType: boolean;
}

/**
 * The URL this state should produce.
 *
 * Only values the customer actually chose are written: a guest count of 1 and
 * step 0 are the defaults every flow starts at, so writing them would put noise
 * in every link, and "0" is Redux's sentinel for an unpicked time.
 */
export function buildQuery(source: QuerySource): BookingQuery {
  const q: BookingQuery = {};
  // Carried straight through from the URL rather than from Redux, because the
  // category is an ENTRY parameter with no Redux home: the customer chose it
  // on the home screen and nothing in the flow can change it.
  //
  // It has to be here at all because this function REBUILDS the query from
  // scratch on every Redux→URL sync, so a booking parameter it does not name
  // is dropped at the first sync (only non-booking parameters are carried
  // over, by `withForeignParams`). That is what would happen to the category
  // otherwise — silently, at step 1 — and every per-category setting
  // (`directBooking`, `eventTypes`, the booking window) would fall back to the
  // use case from there on. The tenant's configuration would look ignored.
  if (source.category) q.category = source.category;
  if (source.city) q.city = source.city;
  if (source.district) q.district = source.district;
  if (source.checkInDate) {
    q.checkInDate = source.checkInDate.format("YYYY-MM-DD");
  }
  if (source.checkOutDate) {
    q.checkOutDate = source.checkOutDate.format("YYYY-MM-DD");
  }
  if (source.timeOfDay && source.startTime && source.startTime !== "0") {
    q.startTime = source.startTime;
  }
  if (source.timeOfDay && source.endTime && source.endTime !== "0") {
    q.endTime = source.endTime;
  }
  if (source.withEventType && source.eventType) q.eventType = source.eventType;
  if (source.numberOfGuests > 1) q.numberOfGuests = source.numberOfGuests;
  // The object a link preselects (the room week view, #3036, opens step 0 with
  // `objectId`) has no Redux home until the list loads at step 1 — exactly the
  // category's situation above. Rebuilt from Redux alone, the first change on
  // step 0 dropped it, and the search then selected nothing. So an `objectId`
  // still waiting for the deferred hydration is carried through from the URL.
  const objectId = objectIdFor(source);
  if (objectId) q.objectId = objectId;
  if (source.step > 0) q.step = source.step;
  return q;
}

/** Object-first (#3035): the URL stopped naming a room while a selection is
 *  still held — the selection has to go, see the URL → Redux effect. */
const selectionOutlivesPinnedRoom = (
  pinObjectId: boolean,
  urlObjectId: string | undefined,
  selectedObjectId: string | null
): boolean => pinObjectId && !urlObjectId && !!selectedObjectId;

/** Which object the URL names: the selection, else one still waiting for the
 *  list, else the room an object-first flow is pinned to (#3035). */
const objectIdFor = (source: QuerySource): string | undefined =>
  source.selectedObjectId ?? source.pendingObjectId ?? source.pinnedObjectId;

export function useBookingUrlState({
  timeOfDay = false,
  eventType: withEventType = false,
  pinObjectId = false,
}: BookingUrlStateOptions = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [hydrated, setHydrated] = useState(false);
  const reconcilingRef = useRef(false);
  const objectHydratedRef = useRef(false);

  const city = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation.city
  );
  const district = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation.district
  );
  const checkInDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const checkOutDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );
  const startTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.startTime
  );
  const endTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.endTime
  );
  const eventType = useSelector(
    (state: RootState) => state.bookingCreation.orderData.eventType
  );
  const numberOfGuests = useSelector(
    (state: RootState) => state.apartment.numberOfGuest
  );
  const objectList = useSelector(
    (state: RootState) => state.apartment.locationApartmentList
  );
  const selectedObjectId = useSelector(
    (state: RootState) => state.apartment.selectedApartment?.id ?? null
  );
  const step = useSelector((state: RootState) => state.apartment.step);

  // URL → Redux reconciliation (every URL change)
  useEffect(() => {
    const q = parseBookingQuery(location.search);
    let dispatched = false;

    const targetCity = q.city ?? null;
    const targetDistrict = q.district ?? null;
    if (targetCity !== city || targetDistrict !== district) {
      dispatch(
        setRoomRentalLocation({ city: targetCity, district: targetDistrict })
      );
      dispatched = true;
    }

    if (
      reconcileDate(
        q.checkInDate ?? null,
        checkInDate ? checkInDate.format("YYYY-MM-DD") : null,
        (value) => dispatch(checkInDateSelected({ checkInDate: value })),
        () => dispatch(checkInDateSelectedClear())
      )
    ) {
      dispatched = true;
    }

    if (
      reconcileDate(
        q.checkOutDate ?? null,
        checkOutDate ? checkOutDate.format("YYYY-MM-DD") : null,
        (value) => dispatch(checkOutDateSelected({ checkOutDate: value })),
        () => dispatch(checkOutDateSelectedClear())
      )
    ) {
      dispatched = true;
    }

    if (timeOfDay) {
      // "0" is the Redux sentinel for "not chosen yet", so an absent URL
      // parameter reconciles TO it rather than being skipped.
      const startChanged = reconcile(q.startTime ?? "0", startTime, (value) =>
        dispatch(startTimeSelected({ startTime: value }))
      );
      const endChanged = reconcile(q.endTime ?? "0", endTime, (value) =>
        dispatch(endTimeSelected({ endTime: value }))
      );
      if (startChanged || endChanged) dispatched = true;
    }

    if (
      withEventType &&
      // `eventType` raw, NOT `eventType ?? null`. Redux can hold `undefined`
      // here while an absent URL parameter resolves to `null`, and the original
      // compared the two unnormalised — so that pair counted as a change and
      // dispatched, which also armed the reconciliation guard for one cycle.
      // Normalising both would have silently swallowed that dispatch.
      reconcile(q.eventType ?? null, eventType, (value) =>
        dispatch(eventTypeSelected({ eventType: value }))
      )
    ) {
      dispatched = true;
    }

    if (
      reconcile(q.numberOfGuests ?? 1, numberOfGuests, (value) =>
        dispatch(setNumberOfGuest(value))
      )
    ) {
      dispatched = true;
    }

    // Clamped, not taken at face value: a link whose earlier answers were
    // dropped must not land on a step that has nothing to show. See
    // `clampStep` — this is the fix for #2630 / the line on #2050.
    if (
      reconcile(clampStep(q), step, (value) =>
        dispatch(apartmentStepSet(value))
      )
    ) {
      dispatched = true;
    }

    // Object-first (#3035): the URL names the room the flow is FOR. When it
    // stops naming one — back to the room list, by the header or by the
    // browser — a selection still held from the dated search must go too.
    // Otherwise the Redux → URL sync below writes it straight back
    // (`objectIdFor` prefers the selection), and the room list can never be
    // reached again.
    if (selectionOutlivesPinnedRoom(pinObjectId, q.objectId, selectedObjectId)) {
      dispatch(clearSelectedApartment());
      dispatched = true;
    }

    if (dispatched) reconcilingRef.current = true;
    if (!hydrated) setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Deferred objectId hydration — the list is not loaded until step >= 1
  useEffect(() => {
    if (!hydrated || objectHydratedRef.current) return;
    if (!objectList || objectList.length === 0) return;

    const q = parseBookingQuery(location.search);
    if (q.objectId) {
      const found = objectList.find((o) => o.id === q.objectId);
      if (found && found.id !== selectedObjectId) {
        dispatch(setSelectedApartment(found));
      } else if (!found && selectedObjectId) {
        dispatch(clearSelectedApartment());
      }
    }
    objectHydratedRef.current = true;
  }, [hydrated, objectList, selectedObjectId, dispatch, location.search]);

  // Redux → URL sync
  useEffect(() => {
    if (!hydrated) return;
    if (reconcilingRef.current) {
      reconcilingRef.current = false;
      return;
    }

    const bookingSearch = stringifyBookingQuery(
      buildQuery({
        // Re-read from the URL each sync, so the parameter is its own source
        // and destination and the two cannot drift apart.
        category: parseBookingQuery(location.search).category,
        city,
        district,
        checkInDate,
        checkOutDate,
        startTime,
        endTime,
        eventType,
        numberOfGuests,
        selectedObjectId,
        pendingObjectId: objectHydratedRef.current
          ? undefined
          : parseBookingQuery(location.search).objectId,
        pinnedObjectId: pinObjectId
          ? parseBookingQuery(location.search).objectId
          : undefined,
        step,
        timeOfDay,
        withEventType,
      })
    );
    // Parameters this hook does not own (the in-place terms and withdrawal
    // sheets, `useInPlaceSheet`) are carried over. Rebuilt from booking keys
    // alone, the sync stripped `?terms=1` and closed the terms sheet the moment
    // it opened. Booking keys still come from Redux only.
    const newSearch = withForeignParams(bookingSearch, location.search);
    const currentSearch = location.search.startsWith("?")
      ? location.search.substring(1)
      : location.search;
    if (newSearch === currentSearch) return;

    const previousQuery = parseBookingQuery(location.search);
    const stepChanged = (previousQuery.step ?? 0) !== step;

    navigate(
      {
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      },
      { replace: !stepChanged }
    );
  }, [
    hydrated,
    city,
    district,
    checkInDate,
    checkOutDate,
    startTime,
    endTime,
    eventType,
    numberOfGuests,
    selectedObjectId,
    step,
    timeOfDay,
    withEventType,
    pinObjectId,
    location.pathname,
    location.search,
    navigate,
  ]);
}
